// src/services/weeklyRecap.service.ts
import { supabaseRpc } from '@/lib/supabase'
import type { WeeklyRecap } from '@/models/WeeklyRecap'

// supabaseRpc (= supabase as any) date d'avant l'entrée de weekly_recaps dans
// database.types.ts. Les lignes sont de toute façon recastées en WeeklyRecap,
// qui donne la forme réelle des colonnes jsonb.

export const weeklyRecapService = {
  /** Récaps de la semaine en cours pour tous les enfants du foyer, status=generated uniquement (spec §6). */
  async getCurrentWeekRecaps(householdId: string): Promise<WeeklyRecap[]> {
    const { data, error } = await supabaseRpc
      .from('weekly_recaps')
      .select('*')
      .eq('household_id', householdId)
      .eq('status', 'generated')
      .order('generated_at', { ascending: false })
    if (error) throw new Error(error.message)
    return (data ?? []) as WeeklyRecap[]
  },

  /**
   * Un bilan précis, par son identifiant. Sert à la célébration du dimanche, qui
   * reçoit le `recap_id` dans le payload de sa notification.
   *
   * `maybeSingle` : la ligne peut avoir disparu (enfant supprimé, foyer quitté)
   * entre la pose de la notification et son affichage, et zéro ligne est alors
   * un cas normal, pas une erreur PostgREST.
   */
  async getRecapById(recapId: string): Promise<WeeklyRecap | null> {
    const { data, error } = await supabaseRpc
      .from('weekly_recaps')
      .select('*')
      .eq('id', recapId)
      .maybeSingle()
    if (error) throw new Error(error.message)
    return (data as WeeklyRecap | null) ?? null
  },

  /**
   * Historique d'un enfant, status=generated uniquement. `limit` applique le
   * gating Harmonia+ (design doc §6 : 4 dernières semaines en tier libre,
   * historique complet en Harmonia+) — omettre `limit` pour l'illimité.
   */
  async getChildHistory(childId: string, opts: { limit?: number } = {}): Promise<WeeklyRecap[]> {
    let query = supabaseRpc
      .from('weekly_recaps')
      .select('*')
      .eq('child_id', childId)
      .eq('status', 'generated')
      .order('week_start', { ascending: false })
    if (opts.limit) query = query.limit(opts.limit)
    const { data, error } = await query
    if (error) throw new Error(error.message)
    return (data ?? []) as WeeklyRecap[]
  },

  /**
   * Écriture opportuniste du fuseau du foyer (design doc §5) — même esprit que
   * le reste du code pour device_push_tokens.timezone. Non bloquante : un
   * échec ne doit jamais empêcher le chargement du dashboard.
   */
  async ensureHouseholdTimezone(householdId: string): Promise<void> {
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone
      const { error } = await supabaseRpc
        .from('households')
        .update({ timezone: detected })
        .eq('id', householdId)
      if (error) console.warn('[weeklyRecap.service] ensureHouseholdTimezone failed (non-blocking)', error)
    } catch (error) {
      console.warn('[weeklyRecap.service] ensureHouseholdTimezone failed (non-blocking)', error)
    }
  },
}

